import React, { useState } from 'react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useAuthStore } from '../store/authStore';
import '../styles/AuthModal.css';

/**
 * ForcePasswordModal — blocks the storefront until an onboarded customer
 * replaces the shared temporary password they were issued at onboarding.
 *
 * Rendered (and gated) purely on `mustChangePassword` from the auth store,
 * which the backend sets on /login and /users/me and clears once
 * POST /users/me/password succeeds. The modal is intentionally NOT
 * dismissible: no close button, no overlay-click, no Escape — the only way
 * out is to set a new password or log out from the navbar.
 */
const ForcePasswordModal: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  const changePassword = useAuthStore((s) => s.changePassword);
  const logout = useAuthStore((s) => s.logout);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isAuthenticated || !mustChangePassword) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 12) {
      // Backend (POST /users/me/password) enforces a 12-character minimum.
      setError('New password must be at least 12 characters');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the current password');
      return;
    }
    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      // Success: store cleared the flag, this modal unmounts on its own.
    } catch (err) {
      // Surface the backend message verbatim (e.g. INVALID_CURRENT_PASSWORD,
      // WEAK_PASSWORD, SAME_PASSWORD) so the customer can correct it.
      setError(err instanceof Error ? err.message : 'Could not update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal__overlay">
      <div className="auth-modal auth-modal--single" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal__right">
          <h2 className="auth-modal__title auth-modal__title--blue">Set a new password</h2>
          <p className="auth-modal__subtitle">Required before you continue</p>

          <div className="auth-modal__demo-hint">
            <strong>Why am I seeing this?</strong>
            <span>Your account was set up with a temporary password. Please choose your own to continue.</span>
          </div>

          {error && <div className="auth-modal__error">{error}</div>}

          <form className="auth-modal__form" onSubmit={handleSubmit}>
            <div className="auth-modal__field">
              <label className="auth-modal__label">Current (temporary) password</label>
              <div className="auth-modal__input-wrap">
                <input
                  className="auth-modal__input"
                  type={showCurrent ? 'text' : 'password'}
                  placeholder="......"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <button type="button" className="auth-modal__toggle" onClick={() => setShowCurrent(!showCurrent)}>
                  {showCurrent ? <MdVisibility /> : <MdVisibilityOff />}
                </button>
              </div>
            </div>
            <div className="auth-modal__field">
              <label className="auth-modal__label">New password</label>
              <div className="auth-modal__input-wrap">
                <input
                  className="auth-modal__input"
                  type={showNew ? 'text' : 'password'}
                  placeholder="At least 12 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button type="button" className="auth-modal__toggle" onClick={() => setShowNew(!showNew)}>
                  {showNew ? <MdVisibility /> : <MdVisibilityOff />}
                </button>
              </div>
            </div>
            <div className="auth-modal__field">
              <label className="auth-modal__label">Confirm new password</label>
              <input
                className="auth-modal__input"
                type={showNew ? 'text' : 'password'}
                placeholder="......"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="auth-modal__submit auth-modal__submit--blue" disabled={loading}>
              {loading ? 'SAVING...' : 'SAVE PASSWORD'}
            </button>
          </form>

          <p className="auth-modal__footer-text">
            NOT YOU ? <button type="button" className="auth-modal__switch" onClick={logout}>LOG OUT</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForcePasswordModal;
